// Quick colors for the custom (variables) storefront scheme: derive the
// storefront color tokens from a handful of seed colors. Pure functions only.

export type StorefrontColorSeeds = {
  primary: string
  accent: string
  background: string
  text: string
  header?: string
  footer?: string
  success?: string
  error?: string
  base?: StorefrontColorBase
}

// Base preset the seeds were derived on. The editor's preset names are 'default' | 'dark'.
export type StorefrontColorBase = 'light' | 'dark'
export type StorefrontColorPreset = 'default' | 'dark'

export type StorefrontTokenData = Record<string, { value: string, isBasic?: boolean }>

export type ContrastCheck = {
  id: string
  label: string
  fg: string
  bg: string
  ratio: number
  min: number
  ok: boolean
}

export const SEED_KEYS = ['primary', 'accent', 'background', 'text', 'header', 'footer', 'success', 'error'] as const
export const REQUIRED_SEED_KEYS = ['primary', 'accent', 'background', 'text'] as const

export const DEFAULT_SUCCESS = '#00840f'
export const DEFAULT_ERROR = '#8c0000'
export const DEFAULT_INFO = '#00d3c4'

export const DEFAULT_SEEDS: StorefrontColorSeeds = {
  primary: '#6a9e29',
  accent: '#01b5dd',
  background: '#ffffff',
  text: '#000000'
}

const ADVANCED_CSS_SENTINEL = '/* johku-editor: advanced */'
const ADVANCED_CSS_SENTINEL_RE = /\/\*\s*johku-editor:\s*advanced\s*\*\//i
const ROOT_BLOCK_RE = /:root\s*\{[\s\S]*?\}/

type Rgb = [number, number, number]

export function normalizeHex (v: unknown): string | null {
  if (typeof v !== 'string') {
    return null
  }
  const s = v.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(s)) {
    return s
  }
  if (/^#[0-9a-f]{3}$/.test(s)) {
    return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3]
  }
  return null
}

export function sanitizeSeeds (input: unknown): StorefrontColorSeeds | null {
  let obj = input
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj)
    } catch (e) {
      return null
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return null
  }
  const out: Record<string, string> = {}
  for (const key of SEED_KEYS) {
    const hex = normalizeHex((obj as Record<string, unknown>)[key])
    if (hex) {
      out[key] = hex
    }
  }
  for (const key of REQUIRED_SEED_KEYS) {
    if (!out[key]) {
      return null
    }
  }
  const base = (obj as Record<string, unknown>).base
  if (typeof base === 'string' && (base.trim().toLowerCase() === 'light' || base.trim().toLowerCase() === 'dark')) {
    out.base = base.trim().toLowerCase()
  }
  return out as unknown as StorefrontColorSeeds
}

export function baseToPreset (base: StorefrontColorBase | null | undefined): StorefrontColorPreset {
  return base === 'dark' ? 'dark' : 'default'
}

export function presetToBase (preset: StorefrontColorPreset | string | null | undefined): StorefrontColorBase {
  return preset === 'dark' ? 'dark' : 'light'
}

function hexToRgb (hex: string): Rgb {
  const h = normalizeHex(hex) || '#000000'
  return [
    parseInt(h.slice(1, 3), 16) / 255,
    parseInt(h.slice(3, 5), 16) / 255,
    parseInt(h.slice(5, 7), 16) / 255
  ]
}

function rgbToHex (rgb: Rgb): string {
  return '#' + rgb.map(c => {
    const n = Math.round(Math.min(1, Math.max(0, c)) * 255)
    return n.toString(16).padStart(2, '0')
  }).join('')
}

function srgbToLinear (c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function linearToSrgb (c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
}

// Björn Ottosson, https://bottosson.github.io/posts/oklab/
function linearRgbToOklab ([r, g, b]: Rgb): Rgb {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s
  ]
}

function oklabToLinearRgb ([L, a, b]: Rgb): Rgb {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3)
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3)
  const s = Math.pow(L - 0.0894841775 * a - 1.2914855480 * b, 3)
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
  ]
}

function lerp (a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

// Same as CSS `color-mix(in oklab, a, b pctB%)`.
export function mixOklab (a: string, b: string, pctB: number): string {
  const labA = linearRgbToOklab(hexToRgb(a).map(srgbToLinear) as Rgb)
  const labB = linearRgbToOklab(hexToRgb(b).map(srgbToLinear) as Rgb)
  const linear = oklabToLinearRgb(lerp(labA, labB, pctB / 100))
  return rgbToHex(linear.map(c => linearToSrgb(Math.min(1, Math.max(0, c)))) as Rgb)
}

// Same as CSS `color-mix(in srgb, a, b pctB%)`.
export function mixSrgb (a: string, b: string, pctB: number): string {
  return rgbToHex(lerp(hexToRgb(a), hexToRgb(b), pctB / 100))
}

// WCAG 2.x relative luminance.
export function relativeLuminance (hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio (a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

export function pickTextColor (bg: string, preferred: string): string {
  if (contrastRatio(preferred, bg) >= 4.5) {
    return preferred
  }
  return contrastRatio('#000000', bg) >= contrastRatio('#ffffff', bg) ? '#000000' : '#ffffff'
}

// Text on UI surfaces (buttons, bars, badges): white whenever it is readable
// enough for large/bold UI text, otherwise the text seed, otherwise black/white.
export function pickUiTextColor (bg: string, preferred: string): string {
  if (contrastRatio('#ffffff', bg) >= 3) {
    return '#ffffff'
  }
  if (contrastRatio(preferred, bg) >= 4.5) {
    return preferred
  }
  return contrastRatio('#000000', bg) >= contrastRatio('#ffffff', bg) ? '#000000' : '#ffffff'
}

// Small text on a UI surface (breadcrumb): as pickUiTextColor, but white needs 4.5:1 too
// (JDEV-3258, mirrors shared/styles/tokens.css).
export function pickSmallUiTextColor (bg: string, preferred: string): string {
  if (contrastRatio('#ffffff', bg) >= 4.5) {
    return '#ffffff'
  }
  if (contrastRatio(preferred, bg) >= 4.5) {
    return preferred
  }
  return contrastRatio('#000000', bg) >= contrastRatio('#ffffff', bg) ? '#000000' : '#ffffff'
}

function isLight (hex: string): boolean {
  return relativeLuminance(hex) > 0.5
}

// currentTokens: the shop's current token values. Only the info colour is read from them: the selector
// has no info seed, and the info button text must stay readable on the shop's own info colour.
export function deriveStorefrontTokens (seeds: StorefrontColorSeeds, baseTokens?: Record<string, string>, currentTokens?: Record<string, string>): Record<string, string> {
  const P = seeds.primary
  const A = seeds.accent
  const B = seeds.background
  const T = seeds.text
  const H = seeds.header || B
  const F = seeds.footer || T
  const S = seeds.success || normalizeHex(baseTokens && baseTokens['--johku-color-success']) || DEFAULT_SUCCESS
  const E = seeds.error || normalizeHex(baseTokens && baseTokens['--johku-color-error']) || DEFAULT_ERROR

  const border = mixOklab(B, T, 8)
  const inputText = pickTextColor(B, T)
  const primaryTint = mixOklab(P, '#000000', 10)
  const orderwrapBg = mixOklab(P, B, 70)
  const navbarText = pickUiTextColor(H, T)
  const titlebarText = pickUiTextColor(primaryTint, T)
  const footerText = pickUiTextColor(F, T)
  const footerBar = mixSrgb(F, footerText, 13)
  const footerBarText = pickUiTextColor(footerBar, T)
  // Breadcrumb sits on a darkened primary (darker than the title bar), not on the accent.
  const breadcrumbBg = mixOklab(P, '#000000', 15)
  const breadcrumbText = pickSmallUiTextColor(breadcrumbBg, T)
  const primaryText = pickUiTextColor(P, T)
  // Menu titles sit on the slide-in menu blocks, the dropdown colour 2% darker (storefront Header.vue .menu-block):
  // the primary if it has 4.5:1 there, else black or white, whichever has more contrast (JDEV-3424, mirrors
  // shared/styles/tokens.css). Draft pick, pending Saku: black or white rather than the text seed.
  const menuPanel = mixOklab(H, '#000000', 2)
  const menuTitle = contrastRatio(P, menuPanel) >= 4.5 ? P : (contrastRatio('#000000', menuPanel) >= contrastRatio('#ffffff', menuPanel) ? '#000000' : '#ffffff')
  const successTint = mixOklab(S, '#000000', 10)
  const errorTint = mixOklab(E, '#000000', 10)
  const successText = pickUiTextColor(S, T)
  const errorText = pickUiTextColor(E, T)
  const successTextHover = mixOklab(successText, '#000000', 5)
  const errorTextHover = mixOklab(errorText, '#000000', 5)
  const I = normalizeHex(currentTokens && currentTokens['--johku-color-info']) || normalizeHex(baseTokens && baseTokens['--johku-color-info']) || DEFAULT_INFO
  const infoText = pickUiTextColor(I, T)

  return {
    '--johku-color-primary': P,
    '--johku-color-secondary': A,
    '--johku-color-background': B,
    '--johku-color-modal-background': B,
    '--johku-color-text': T,
    '--johku-color-background-tint': mixOklab(B, '#000000', 5),
    '--johku-color-border': border,
    '--johku-color-input-background': mixOklab(B, T, 5),
    '--johku-color-input-text': inputText,
    '--johku-color-orderwrap-input-background': B,
    '--johku-color-orderwrap-input-text': inputText,
    '--johku-color-primary-tint': primaryTint,
    '--johku-color-secondary-tint': mixOklab(A, '#000000', 10),
    '--johku-color-button-primary-text': primaryText,
    '--johku-color-button-secondary-text': pickUiTextColor(A, T),
    '--johku-color-breadcrumb-background': breadcrumbBg,
    '--johku-color-breadcrumb-text': breadcrumbText,
    '--johku-color-breadcrumb-text-link': breadcrumbText,
    '--johku-color-orderwrap-background': orderwrapBg,
    '--johku-color-orderwrap-text': pickTextColor(orderwrapBg, T),
    '--johku-color-navbar-background': H,
    '--johku-color-navbar-text': navbarText,
    '--johku-color-navbar-dropdown-background': H,
    '--johku-color-navbar-dropdown-text': navbarText,
    '--johku-color-navbar-dropdown-link': navbarText,
    '--johku-color-navbar-dropdown-title': menuTitle,
    '--johku-color-dropdown-background': B,
    '--johku-color-navbar-cart-count-background': P,
    '--johku-color-navbar-cart-count-text': primaryText,
    '--johku-color-titlebar-background': primaryTint,
    '--johku-color-titlebar-text-title': titlebarText,
    '--johku-color-titlebar-text-shadow': isLight(titlebarText) ? '#000000' : '#ffffff',
    '--johku-color-panel-background': B,
    '--johku-color-panel-text': pickTextColor(B, T),
    '--johku-color-panel-border': border,
    '--johku-color-footer-background': F,
    '--johku-color-footer-text': footerText,
    '--johku-color-footer-link': footerText,
    '--johku-color-footer-bar-background': footerBar,
    '--johku-color-footer-bar-text': footerBarText,
    '--johku-color-footer-bar-link': footerBarText,
    '--johku-footer-nav-separator': isLight(footerText) ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
    '--johku-color-success': S,
    '--johku-color-success-tint': successTint,
    '--johku-color-button-success-background': S,
    '--johku-color-button-success-border': successTint,
    '--johku-color-button-success-border-hover': successTint,
    '--johku-color-button-success-background-hover': successTint,
    '--johku-color-button-success-text': successText,
    '--johku-color-button-success-text-hover': successTextHover,
    '--johku-color-error': E,
    '--johku-color-danger': E,
    '--johku-color-error-tint': errorTint,
    '--johku-color-danger-tint': errorTint,
    '--johku-color-button-error-background': E,
    '--johku-color-button-error-border': errorTint,
    '--johku-color-button-error-border-hover': errorTint,
    '--johku-color-button-error-background-hover': errorTint,
    '--johku-color-button-error-text': errorText,
    '--johku-color-button-error-text-hover': errorTextHover,
    '--johku-color-button-danger-background': E,
    '--johku-color-button-danger-border': errorTint,
    '--johku-color-button-danger-border-hover': errorTint,
    '--johku-color-button-danger-background-hover': errorTint,
    '--johku-color-button-danger-text': errorText,
    '--johku-color-button-danger-text-hover': errorTextHover,
    '--johku-color-button-info-text': infoText,
    '--johku-color-button-info-text-hover': mixOklab(infoText, '#000000', 5)
  }
}

export function contrastChecks (seeds: StorefrontColorSeeds, tokens: Record<string, string>): ContrastCheck[] {
  const t = (key: string, fallback: string) => normalizeHex(tokens[key]) || fallback
  const B = t('--johku-color-background', seeds.background)
  const A = t('--johku-color-secondary', seeds.accent)
  const P = t('--johku-color-primary', seeds.primary)
  const S = t('--johku-color-success', seeds.success || DEFAULT_SUCCESS)
  const E = t('--johku-color-error', seeds.error || DEFAULT_ERROR)
  const orderwrapBg = t('--johku-color-orderwrap-background', B)
  const pairs: Array<[string, string, string, string, number]> = [
    ['text', 'Body text', t('--johku-color-text', seeds.text), B, 4.5],
    ['link', 'Links', A, B, 4.5],
    ['title', 'Titles', A, B, 3],
    ['button-primary', 'Text on buttons (primary)', t('--johku-color-button-primary-text', '#ffffff'), P, 3],
    ['button-secondary', 'Text on buttons (accent)', t('--johku-color-button-secondary-text', '#ffffff'), A, 3],
    ['button-success', 'Text on buttons (success)', t('--johku-color-button-success-text', '#ffffff'), S, 3],
    ['button-error', 'Text on buttons (error)', t('--johku-color-button-error-text', '#ffffff'), E, 3],
    ['cart-badge', 'Cart badge text', t('--johku-color-navbar-cart-count-text', '#ffffff'), t('--johku-color-navbar-cart-count-background', P), 3],
    ['navbar', 'Navigation bar text', t('--johku-color-navbar-text', '#ffffff'), t('--johku-color-navbar-background', B), 3],
    ['orderwrap', 'Action area text', t('--johku-color-orderwrap-text', seeds.text), orderwrapBg, 4.5],
    ['price', 'Prices in the action area', P, orderwrapBg, 3],
    ['titlebar', 'Title bar text', t('--johku-color-titlebar-text-title', '#ffffff'), t('--johku-color-titlebar-background', P), 3],
    ['breadcrumb', 'Breadcrumb text', t('--johku-color-breadcrumb-text', '#ffffff'), t('--johku-color-breadcrumb-background', mixOklab(P, '#000000', 15)), 4.5],
    ['footer', 'Footer text', t('--johku-color-footer-text', '#ffffff'), t('--johku-color-footer-background', seeds.text), 3],
    ['success-text', 'Success text', S, B, 4.5],
    ['error-text', 'Error text', E, B, 4.5]
  ]
  return pairs.map(([id, label, fg, bg, min]) => {
    const exact = contrastRatio(fg, bg)
    return { id, label, fg, bg, ratio: Math.round(exact * 100) / 100, min, ok: exact >= min }
  })
}

function toRootBlock (tokenData: StorefrontTokenData): string {
  let css = ':root {\n'
  for (const key in tokenData) {
    if (Object.hasOwnProperty.call(tokenData, key)) {
      css += `    ${key}: ${tokenData[key].value};\n`
    }
  }
  return css + '}'
}

export function buildQuickColorsCss (tokenData: StorefrontTokenData, existingCss: string | null): string {
  const rootBlock = toRootBlock(tokenData)
  if (typeof existingCss !== 'string' || !ADVANCED_CSS_SENTINEL_RE.test(existingCss)) {
    return rootBlock
  }
  const rest = existingCss
    .replace(new RegExp(ADVANCED_CSS_SENTINEL_RE.source, 'gi'), '')
    .replace(ROOT_BLOCK_RE, '')
    .trim()
  return ADVANCED_CSS_SENTINEL + '\n' + rootBlock + (rest ? '\n\n' + rest : '')
}

// baseTokens: the base preset's token values. Optional seeds are set only when
// they differ from the default derive() would use: header vs background,
// footer vs text, success/error vs the base preset (or the built-in fallback).
export function seedsFromTokens (tokenData: StorefrontTokenData | null | undefined, baseTokens?: Record<string, string>): StorefrontColorSeeds {
  const v = (key: string) => normalizeHex(tokenData && tokenData[key] ? tokenData[key].value : null)
  const seeds: StorefrontColorSeeds = {
    primary: v('--johku-color-primary') || DEFAULT_SEEDS.primary,
    accent: v('--johku-color-secondary') || DEFAULT_SEEDS.accent,
    background: v('--johku-color-background') || DEFAULT_SEEDS.background,
    text: v('--johku-color-text') || DEFAULT_SEEDS.text
  }
  const header = v('--johku-color-navbar-background')
  const footer = v('--johku-color-footer-background')
  if (header && header !== seeds.background) {
    seeds.header = header
  }
  if (footer && footer !== seeds.text) {
    seeds.footer = footer
  }
  const success = v('--johku-color-success')
  const error = v('--johku-color-error')
  if (success && success !== (normalizeHex(baseTokens && baseTokens['--johku-color-success']) || DEFAULT_SUCCESS)) {
    seeds.success = success
  }
  if (error && error !== (normalizeHex(baseTokens && baseTokens['--johku-color-error']) || DEFAULT_ERROR)) {
    seeds.error = error
  }
  return seeds
}

// Resolve a token to a hex color through var() references and simple
// two-color color-mix() values. Returns null when it can't be resolved.
export function resolveTokenColor (tokens: Record<string, string>, key: string, depth = 0): string | null {
  if (depth > 4 || !tokens) {
    return null
  }
  const raw = tokens[key]
  if (typeof raw !== 'string') {
    return null
  }
  const resolveValue = (value: string): string | null => {
    const v = value.trim()
    const hex = normalizeHex(v)
    if (hex) {
      return hex
    }
    const ref = v.match(/^var\(\s*(--[\w-]+)\s*\)$/)
    return ref ? resolveTokenColor(tokens, ref[1], depth + 1) : null
  }
  const mix = raw.match(/^color-mix\(\s*in\s+(oklab|srgb)\s*,\s*([^,]+?)\s*,\s*([^,]+?)\s+(\d+(?:\.\d+)?)%\s*\)$/)
  if (mix) {
    const a = resolveValue(mix[2])
    const b = resolveValue(mix[3])
    if (!a || !b) {
      return null
    }
    return mix[1] === 'oklab' ? mixOklab(a, b, parseFloat(mix[4])) : mixSrgb(a, b, parseFloat(mix[4]))
  }
  return resolveValue(raw)
}
